import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, NgForm } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';

import { Ga4AnalyticsService } from '../../../core/analytics/ga4-analytics.service';
import { AuthStateService } from '../../../core/auth/auth-state.service';
import {
  NumeronActionModel,
  NumeronCollectionModel,
  NumeronItemModel,
} from '../models/numeron.model';
import {
  AddNumeronToCollectionPayload,
  CreateNumeronPayload,
  NumeronsService,
  UpdateCollectionNumeronPayload,
  UpdateNumeronPayload,
} from '../services/numerons.service';
import { TopicApiModel } from '../../topics/models/topic.model';
import { TopicsService } from '../../topics/services/topics.service';

const COLLECTION_MODE_ENABLED_FLAG = 16;
const DEFAULT_COLLECTION_TYPE = 'Standard';
const PAGE_MODE_CREATE = 'create';
const PAGE_MODE_EDIT = 'edit';
const PAGE_MODE_ADD_TO_COLLECTION = 'add-to-collection';
const PAGE_MODE_EDIT_IN_COLLECTION = 'edit-in-collection';

type EditNumeronTab = 'media' | 'source' | 'categories' | 'actions';
type EditNumeronPageMode =
  | typeof PAGE_MODE_CREATE
  | typeof PAGE_MODE_EDIT
  | typeof PAGE_MODE_ADD_TO_COLLECTION
  | typeof PAGE_MODE_EDIT_IN_COLLECTION;

interface EditNumeronActionFormModel {
  enabled: boolean;
  name: string;
  value: string;
}

interface EditNumeronFormModel {
  numeral: string;
  title: string;
  visibility: boolean;
  assetPath: string;
  subType: string;
  source: string;
  url: string;
  topicId: string;
  categories: string;
  action1: EditNumeronActionFormModel;
  action2: EditNumeronActionFormModel;
  action3: EditNumeronActionFormModel;
  action4: EditNumeronActionFormModel;
  action5: EditNumeronActionFormModel;
}

interface AddToCollectionContext {
  collectionId: string;
  title: string;
  type: string;
  source: string;
  url: string;
  categories: string;
  relatedContentURL: string;
  assetPath: string;
  visibility: boolean;
  modeEnabled: boolean;
}

@Component({
  selector: 'app-edit-numeron-page',
  imports: [FormsModule, RouterLink],
  templateUrl: './edit-numeron-page.component.html',
})
export class EditNumeronPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly title = inject(Title);
  private readonly ga4AnalyticsService = inject(Ga4AnalyticsService);
  private readonly numeronsService = inject(NumeronsService);
  private readonly topicsService = inject(TopicsService);
  private readonly authStateService = inject(AuthStateService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly activeTab = signal<EditNumeronTab>('media');
  protected readonly loadErrorMessage = signal('');
  protected readonly submitErrorMessage = signal('');
  protected readonly topicLoadErrorMessage = signal('');
  protected readonly isLoading = signal(true);
  protected readonly isSubmitting = signal(false);
  protected readonly isSubmitAttempted = signal(false);
  protected readonly pageMode = (this.route.snapshot.data['mode'] ??
    PAGE_MODE_EDIT) as EditNumeronPageMode;
  protected readonly isCreateMode = this.pageMode === PAGE_MODE_CREATE;
  protected readonly isAddToCollectionMode = this.pageMode === PAGE_MODE_ADD_TO_COLLECTION;
  protected readonly isEditInCollectionMode = this.pageMode === PAGE_MODE_EDIT_IN_COLLECTION;
  protected readonly collectionId = this.route.snapshot.paramMap.get('collectionId') ?? '';
  protected readonly targetNumeronId = this.route.snapshot.paramMap.get('numeronId') ?? '';
  protected readonly cancelHref = this.resolveCancelHref();
  protected readonly topicOptions = signal<TopicApiModel[]>([]);
  protected readonly currentTopicLabel = signal('');
  protected readonly selectedFile = signal<File | null>(null);
  protected readonly selectedFileName = signal('');
  protected readonly loadedCollection = signal<NumeronCollectionModel | null>(null);
  protected readonly collectionContext = signal<AddToCollectionContext | null>(null);

  protected readonly numeticonOptions = [
    { value: 'panel-happy', label: 'Happy' },
    { value: 'panel-royal', label: 'Royal' },
    { value: 'panel-positive', label: 'Positive' },
    { value: 'panel-calm', label: 'Calm' },
    { value: 'panel-sad', label: 'Sad' },
    { value: 'panel-angry', label: 'Angry' },
    { value: 'panel-formal', label: 'Formal' },
  ];

  protected readonly formModel: EditNumeronFormModel = {
    numeral: '',
    title: '',
    visibility: false,
    assetPath: '',
    subType: '',
    source: '',
    url: '',
    topicId: '',
    categories: '',
    action1: {
      enabled: false,
      name: 'Call Now',
      value: '',
    },
    action2: {
      enabled: false,
      name: 'Send Message',
      value: '',
    },
    action3: {
      enabled: false,
      name: '',
      value: '',
    },
    action4: {
      enabled: false,
      name: '',
      value: '',
    },
    action5: {
      enabled: false,
      name: '',
      value: '',
    },
  };

  protected readonly resolvedTopicOptions = computed(() => {
    const fetchedTopicOptions = this.topicOptions().map((topic) => ({
      value: topic._id,
      label: topic.topic,
    }));
    const currentTopicId = this.formModel.topicId.trim();
    const currentTopicLabel = this.currentTopicLabel().trim();
    const hasCurrentTopic = fetchedTopicOptions.some((topic) => topic.value === currentTopicId);

    return [
      { value: '', label: 'Select a topic' },
      ...(currentTopicId && currentTopicLabel && !hasCurrentTopic
        ? [{ value: currentTopicId, label: currentTopicLabel }]
        : []),
      ...fetchedTopicOptions,
    ];
  });

  public constructor() {
    this.title.setTitle(this.getPageTitle());
    this.ga4AnalyticsService.trackPageView({
      contentId: this.isCreateMode ? undefined : this.collectionId || undefined,
      contentType: this.getPageTitle(),
      contentGroup: 'My Numerons',
    });
    this.loadTopics();
    if (this.isCreateMode) {
      this.isLoading.set(false);
      return;
    }

    if (this.isAddToCollectionMode) {
      this.loadAddToCollectionContext();
      return;
    }

    if (this.isEditInCollectionMode) {
      this.loadEditInCollectionContext();
      return;
    }

    this.loadCollection();
  }

  protected setActiveTab(tab: EditNumeronTab): void {
    this.activeTab.set(tab);
  }

  protected isActiveTab(tab: EditNumeronTab): boolean {
    return this.activeTab() === tab;
  }

  protected canEdit(): boolean {
    return !this.isLoading() && this.loadErrorMessage() === '';
  }

  protected onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    this.selectedFile.set(file);
    this.selectedFileName.set(file?.name ?? '');
    if (file) {
      this.formModel.assetPath = '';
      this.formModel.subType = '';
    }
    this.submitErrorMessage.set('');
  }

  protected onAssetPathChanged(): void {
    if (this.formModel.assetPath.trim()) {
      this.selectedFile.set(null);
      this.selectedFileName.set('');
      this.formModel.subType = '';
    }
    this.submitErrorMessage.set('');
  }

  protected onSubTypeChanged(): void {
    if (this.formModel.subType.trim()) {
      this.selectedFile.set(null);
      this.selectedFileName.set('');
      this.formModel.assetPath = '';
    }
    this.submitErrorMessage.set('');
  }

  protected hasMediaSelection(): boolean {
    return this.resolveAssetMode() !== 'none';
  }

  protected hasActionValidationError(): boolean {
    return (
      this.isFixedActionInvalid(this.formModel.action1) ||
      this.isFixedActionInvalid(this.formModel.action2) ||
      this.isCustomActionInvalid(this.formModel.action3) ||
      this.isCustomActionInvalid(this.formModel.action4) ||
      this.isCustomActionInvalid(this.formModel.action5)
    );
  }

  protected showMediaError(): boolean {
    return this.isSubmitAttempted() && !this.hasMediaSelection();
  }

  protected showActionValueError(action: EditNumeronActionFormModel): boolean {
    return this.isSubmitAttempted() && this.isFixedActionInvalid(action);
  }

  protected showCustomActionNameError(action: EditNumeronActionFormModel): boolean {
    return this.isSubmitAttempted() && action.enabled && action.name.trim().length === 0;
  }

  protected showCustomActionValueError(action: EditNumeronActionFormModel): boolean {
    return this.isSubmitAttempted() && action.enabled && action.value.trim().length === 0;
  }

  protected submitButtonLabel(): string {
    if (this.isSubmitting()) {
      if (this.isCreateMode) {
        return 'Creating...';
      }

      if (this.isAddToCollectionMode) {
        return 'Adding...';
      }

      return 'Updating...';
    }

    if (this.isCreateMode) {
      return 'Create Numeron';
    }

    if (this.isAddToCollectionMode) {
      return 'Add Numeron';
    }

    return 'Update Numeron';
  }

  protected showValidationSummary(form: NgForm): boolean {
    return (
      form.submitted &&
      (form.invalid || !this.hasMediaSelection() || this.hasActionValidationError())
    );
  }

  protected async onSubmit(form: NgForm): Promise<void> {
    this.isSubmitAttempted.set(true);
    this.submitErrorMessage.set('');

    if (!this.canEdit()) {
      return;
    }

    if (form.invalid || !this.hasMediaSelection() || this.hasActionValidationError()) {
      if (!this.hasMediaSelection()) {
        this.setActiveTab('media');
      } else if (!this.formModel.topicId.trim()) {
        this.setActiveTab('categories');
      } else if (this.hasActionValidationError()) {
        this.setActiveTab('actions');
      }

      form.control.markAllAsTouched();
      return;
    }

    this.isSubmitting.set(true);

    if (this.isCreateMode) {
      this.numeronsService
        .createNumeron(this.buildCreatePayload())
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: async () => {
            this.isSubmitting.set(false);
            await this.router.navigateByUrl('/mynumerons');
          },
          error: () => {
            this.isSubmitting.set(false);
            this.submitErrorMessage.set('Oops, could not create Numeron!');
          },
        });
      return;
    }

    if (this.isAddToCollectionMode) {
      const collectionContext = this.collectionContext();
      if (!collectionContext) {
        this.isSubmitting.set(false);
        this.submitErrorMessage.set(
          'Collection details are unavailable right now. Please try again.',
        );
        return;
      }

      this.numeronsService
        .addNumeronToCollection(this.buildAddToCollectionPayload(collectionContext))
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: async (collection) => {
            this.isSubmitting.set(false);
            await this.router.navigateByUrl(`/mynumerons/collection/${collection._id}`);
          },
          error: () => {
            this.isSubmitting.set(false);
            this.submitErrorMessage.set('Oops, could not add Numeron to Collection!');
          },
        });
      return;
    }

    if (this.isEditInCollectionMode) {
      const collectionContext = this.collectionContext();
      if (!collectionContext) {
        this.isSubmitting.set(false);
        this.submitErrorMessage.set(
          'Collection details are unavailable right now. Please try again.',
        );
        return;
      }

      this.numeronsService
        .updateCollectionNumeron(this.buildEditInCollectionPayload(collectionContext))
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: async (collection) => {
            this.isSubmitting.set(false);
            await this.router.navigateByUrl(`/mynumerons/collection/${collection._id}`);
          },
          error: () => {
            this.isSubmitting.set(false);
            this.submitErrorMessage.set('Oops, could not update Numeron!');
          },
        });
      return;
    }

    const collection = this.loadedCollection();
    if (!collection) {
      this.isSubmitting.set(false);
      this.submitErrorMessage.set('Numeron details are unavailable right now. Please try again.');
      return;
    }

    this.numeronsService
      .updateNumeron(this.buildUpdatePayload(collection))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: async () => {
          this.isSubmitting.set(false);
          await this.router.navigateByUrl(this.cancelHref);
        },
        error: () => {
          this.isSubmitting.set(false);
          this.submitErrorMessage.set('Oops, could not update Numeron!');
        },
      });
  }

  private resolveCancelHref(): string {
    if ((this.isAddToCollectionMode || this.isEditInCollectionMode) && this.collectionId) {
      return `/mynumerons/collection/${this.collectionId}`;
    }

    const returnTo = this.route.snapshot.queryParamMap.get('returnTo')?.trim() ?? '';
    return returnTo.startsWith('/mynumerons') ? returnTo : '/mynumerons';
  }

  private getPageTitle(): string {
    if (this.isCreateMode) {
      return 'New Numeron';
    }

    if (this.isAddToCollectionMode) {
      return 'Add Numeron';
    }

    if (this.isEditInCollectionMode) {
      return 'Edit Numeron';
    }

    return 'Edit Numeron';
  }

  private loadTopics(): void {
    this.topicsService
      .getTopics()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (topics) => {
          this.topicOptions.set(topics);
          this.topicLoadErrorMessage.set('');
        },
        error: () => {
          this.topicOptions.set([]);
          this.topicLoadErrorMessage.set(
            'Topics are unavailable right now. You can still review the existing topic.',
          );
        },
      });
  }

  private loadCollection(): void {
    if (!this.collectionId) {
      this.loadErrorMessage.set('Numeron details are unavailable right now. Please try again.');
      this.isLoading.set(false);
      return;
    }

    this.isLoading.set(true);
    this.loadErrorMessage.set('');

    this.numeronsService
      .getMyNumeronCollection(this.collectionId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (collection) => {
          const numeron = collection.numerons[0];
          if (!numeron) {
            this.loadErrorMessage.set(
              'Numeron details are unavailable right now. Please try again.',
            );
            this.isLoading.set(false);
            return;
          }

          if (!this.isPublisherMatch(collection, numeron)) {
            this.loadErrorMessage.set('You can edit only Numerons published by your account.');
            this.isLoading.set(false);
            return;
          }

          this.loadedCollection.set(collection);
          this.populateForm(collection, numeron);
          this.isLoading.set(false);
        },
        error: () => {
          this.loadErrorMessage.set('Numeron details are unavailable right now. Please try again.');
          this.isLoading.set(false);
        },
      });
  }

  private loadAddToCollectionContext(): void {
    this.isLoading.set(true);
    this.loadErrorMessage.set('');

    if (!this.collectionId) {
      this.loadErrorMessage.set('Collection details are unavailable right now. Please try again.');
      this.isLoading.set(false);
      return;
    }

    this.numeronsService
      .getMyNumeronCollection(this.collectionId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (collection) => {
          if (!collection.isCollection) {
            this.loadErrorMessage.set(
              'Collection details are unavailable right now. Please try again.',
            );
            this.isLoading.set(false);
            return;
          }

          if (!this.isPublisherMatch(collection, collection.numerons[0])) {
            this.loadErrorMessage.set(
              'You can add Numerons only to Collections published by your account.',
            );
            this.isLoading.set(false);
            return;
          }

          this.collectionContext.set(this.mapCollectionToAddContext(collection));
          this.applyAddToCollectionDefaults(collection);
          this.isLoading.set(false);
        },
        error: () => {
          this.loadErrorMessage.set(
            'Collection details are unavailable right now. Please try again.',
          );
          this.isLoading.set(false);
        },
      });
  }

  private loadEditInCollectionContext(): void {
    this.isLoading.set(true);
    this.loadErrorMessage.set('');

    if (!this.collectionId || !this.targetNumeronId) {
      this.loadErrorMessage.set('Numeron details are unavailable right now. Please try again.');
      this.isLoading.set(false);
      return;
    }

    this.numeronsService
      .getMyNumeronCollection(this.collectionId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (collection) => {
          if (!collection.isCollection) {
            this.loadErrorMessage.set(
              'Collection details are unavailable right now. Please try again.',
            );
            this.isLoading.set(false);
            return;
          }

          const numeron =
            collection.numerons.find((item) => item._id === this.targetNumeronId) ?? null;
          if (!numeron) {
            this.loadErrorMessage.set(
              'Numeron details are unavailable right now. Please try again.',
            );
            this.isLoading.set(false);
            return;
          }

          if (!this.isSpecificNumeronPublisherMatch(numeron)) {
            this.loadErrorMessage.set('You can edit only Numerons published by your account.');
            this.isLoading.set(false);
            return;
          }

          this.collectionContext.set(this.mapCollectionToAddContext(collection));
          this.populateForm(collection, numeron);
          this.isLoading.set(false);
        },
        error: () => {
          this.loadErrorMessage.set('Numeron details are unavailable right now. Please try again.');
          this.isLoading.set(false);
        },
      });
  }

  private isPublisherMatch(
    collection: NumeronCollectionModel,
    numeron?: NumeronItemModel,
  ): boolean {
    const authenticatedPublisherId = this.authStateService.authUser()?.backendUserId?.trim() ?? '';
    const authenticatedDisplayName = this.authStateService.authUser()?.displayName?.trim() ?? '';
    const collectionPublisherId = collection.user?._id?.trim() ?? '';
    const numeronPublisherId = numeron?.user?._id?.trim() ?? '';
    const collectionPublisherName = collection.user?.displayName?.trim() ?? '';
    const numeronPublisherName = numeron?.user?.displayName?.trim() ?? '';

    if (authenticatedPublisherId) {
      return (
        authenticatedPublisherId === collectionPublisherId ||
        authenticatedPublisherId === numeronPublisherId
      );
    }

    if (authenticatedDisplayName) {
      return (
        authenticatedDisplayName === collectionPublisherName ||
        authenticatedDisplayName === numeronPublisherName
      );
    }

    return true;
  }

  private isSpecificNumeronPublisherMatch(numeron: NumeronItemModel): boolean {
    const authenticatedPublisherId = this.authStateService.authUser()?.backendUserId?.trim() ?? '';
    const authenticatedDisplayName = this.authStateService.authUser()?.displayName?.trim() ?? '';
    const numeronPublisherId = numeron.user?._id?.trim() ?? '';
    const numeronPublisherName = numeron.user?.displayName?.trim() ?? '';

    if (authenticatedPublisherId) {
      return authenticatedPublisherId === numeronPublisherId;
    }

    if (authenticatedDisplayName) {
      return authenticatedDisplayName === numeronPublisherName;
    }

    return true;
  }

  private populateForm(collection: NumeronCollectionModel, numeron: NumeronItemModel): void {
    const numeronType = Number(numeron.type);

    this.selectedFile.set(null);
    this.selectedFileName.set('');
    this.currentTopicLabel.set(
      numeron.topic?.topic?.trim() || collection.topic?.topic?.trim() || '',
    );
    this.formModel.numeral = numeron.numeral;
    this.formModel.title = numeron.title;
    this.formModel.visibility = Boolean(numeron.visibility ?? collection.visibility ?? false);
    this.formModel.assetPath = numeronType === 0 ? (numeron.assetPath?.trim() ?? '') : '';
    this.formModel.subType = numeronType === 1 ? (numeron.subType?.trim() ?? '') : '';
    this.formModel.source = numeron.source ?? '';
    this.formModel.url = numeron.url?.trim() ?? '';
    this.formModel.topicId = numeron.topic?._id ?? collection.topic?._id ?? '';
    this.formModel.categories = (numeron.categories ?? collection.categories ?? []).join(', ');

    const mappedActions = this.mapActions(numeron.actions ?? collection.actions ?? []);
    this.formModel.action1 = mappedActions.action1;
    this.formModel.action2 = mappedActions.action2;
    this.formModel.action3 = mappedActions.action3;
    this.formModel.action4 = mappedActions.action4;
    this.formModel.action5 = mappedActions.action5;
  }

  private buildUpdatePayload(collection: NumeronCollectionModel): UpdateNumeronPayload {
    return {
      collectionId: this.collectionId,
      ...this.buildBasePayload({
        collectionType: collection.type?.trim() || DEFAULT_COLLECTION_TYPE,
        relatedContentURL: collection.relatedContentURL,
        modeEnabled:
          ((collection.mode ?? 0) & COLLECTION_MODE_ENABLED_FLAG) === COLLECTION_MODE_ENABLED_FLAG,
      }),
    };
  }

  private buildCreatePayload(): CreateNumeronPayload {
    return this.buildBasePayload({
      collectionType: DEFAULT_COLLECTION_TYPE,
      relatedContentURL: undefined,
      modeEnabled: false,
    });
  }

  private buildAddToCollectionPayload(
    collectionContext: AddToCollectionContext,
  ): AddNumeronToCollectionPayload {
    const assetMode = this.resolveAssetMode();

    return {
      collectionId: collectionContext.collectionId,
      collectionTitle: collectionContext.title,
      topicId: this.formModel.topicId.trim(),
      collectionType: collectionContext.type,
      collectionSource: collectionContext.source,
      collectionURL: collectionContext.url,
      collectionCategories: collectionContext.categories,
      relatedContentURL: collectionContext.relatedContentURL,
      collectionAssetPath: collectionContext.assetPath,
      collectionVisibility: collectionContext.visibility,
      collectionModeEnabled: collectionContext.modeEnabled,
      numeral: this.formModel.numeral.trim(),
      title: this.formModel.title.trim(),
      source: this.formModel.source,
      url: this.formModel.url,
      categories: this.normalizeCategories(this.formModel.categories),
      file: this.selectedFile(),
      assetPath: assetMode === 'external-url' ? this.formModel.assetPath.trim() : '',
      subType: assetMode === 'numeticon' ? this.formModel.subType.trim() : '',
      numeronActions: JSON.stringify(this.buildActions()),
    };
  }

  private buildEditInCollectionPayload(
    collectionContext: AddToCollectionContext,
  ): UpdateCollectionNumeronPayload {
    return {
      numeronId: this.targetNumeronId,
      ...this.buildAddToCollectionPayload(collectionContext),
    };
  }

  private buildBasePayload(config: {
    collectionType: string;
    relatedContentURL?: string;
    modeEnabled: boolean;
  }): CreateNumeronPayload {
    const assetMode = this.resolveAssetMode();

    return {
      numeral: this.formModel.numeral.trim(),
      title: this.formModel.title.trim(),
      topicId: this.formModel.topicId.trim(),
      collectionType: config.collectionType,
      source: this.formModel.source,
      url: this.formModel.url,
      categories: this.normalizeCategories(this.formModel.categories),
      relatedContentURL: config.relatedContentURL,
      visibility: this.formModel.visibility,
      modeEnabled: config.modeEnabled,
      file: this.selectedFile(),
      assetPath: assetMode === 'external-url' ? this.formModel.assetPath.trim() : '',
      subType: assetMode === 'numeticon' ? this.formModel.subType.trim() : '',
    };
  }

  private mapCollectionToAddContext(collection: NumeronCollectionModel): AddToCollectionContext {
    return {
      collectionId: collection._id,
      title: collection.title ?? '',
      type: collection.type?.trim() || DEFAULT_COLLECTION_TYPE,
      source: collection.source?.trim() ?? '',
      url: collection.url?.trim() ?? '',
      categories: (collection.categories ?? []).join(', '),
      relatedContentURL: collection.relatedContentURL?.trim() ?? '',
      assetPath: collection.assetPath?.trim() ?? '',
      visibility: Boolean(collection.visibility ?? false),
      modeEnabled:
        ((collection.mode ?? 0) & COLLECTION_MODE_ENABLED_FLAG) === COLLECTION_MODE_ENABLED_FLAG,
    };
  }

  private applyAddToCollectionDefaults(collection: NumeronCollectionModel): void {
    if (!this.formModel.source.trim()) {
      this.formModel.source = collection.source?.trim() ?? '';
    }

    if (!this.formModel.url.trim()) {
      this.formModel.url = collection.url?.trim() ?? '';
    }

    if (!this.formModel.topicId.trim()) {
      this.formModel.topicId = collection.topic?._id ?? '';
      this.currentTopicLabel.set(collection.topic?.topic?.trim() ?? '');
    }

    if (!this.formModel.categories.trim()) {
      this.formModel.categories = (collection.categories ?? []).join(', ');
    }
  }

  private normalizeCategories(value: string): string {
    return value
      .split(',')
      .map((category) => category.trim())
      .filter((category) => category.length > 0)
      .join(',');
  }

  private resolveAssetMode(): 'file' | 'external-url' | 'numeticon' | 'none' {
    if (this.selectedFile()) {
      return 'file';
    }

    if (this.formModel.assetPath.trim()) {
      return 'external-url';
    }

    if (this.formModel.subType.trim()) {
      return 'numeticon';
    }

    return 'none';
  }

  private buildActions(): NumeronActionModel[] {
    const actions: NumeronActionModel[] = [];

    if (this.formModel.action1.enabled) {
      actions.push({
        actionInternal: true,
        identifier: 'CallNow',
        name: 'Call Now',
        url: this.formModel.action1.value.trim(),
      });
    }

    if (this.formModel.action2.enabled) {
      actions.push({
        actionInternal: true,
        identifier: 'SendMessage',
        name: 'Send Message',
        url: this.formModel.action2.value.trim(),
      });
    }

    for (const action of [this.formModel.action3, this.formModel.action4, this.formModel.action5]) {
      if (!action.enabled) {
        continue;
      }

      actions.push({
        actionInternal: false,
        identifier: action.name.trim(),
        name: action.name.trim(),
        url: action.value.trim(),
      });
    }

    return actions;
  }

  private isFixedActionInvalid(action: EditNumeronActionFormModel): boolean {
    return action.enabled && action.value.trim().length === 0;
  }

  private isCustomActionInvalid(action: EditNumeronActionFormModel): boolean {
    return action.enabled && (action.name.trim().length === 0 || action.value.trim().length === 0);
  }

  private mapActions(
    actions: NumeronActionModel[],
  ): Pick<EditNumeronFormModel, 'action1' | 'action2' | 'action3' | 'action4' | 'action5'> {
    const defaults = this.createDefaultActions();
    const customActions: NumeronActionModel[] = [];

    for (const action of actions) {
      if (action.identifier === 'CallNow') {
        defaults.action1 = {
          enabled: true,
          name: 'Call Now',
          value: action.url?.trim() ?? '',
        };
        continue;
      }

      if (action.identifier === 'SendMessage') {
        defaults.action2 = {
          enabled: true,
          name: 'Send Message',
          value: action.url?.trim() ?? '',
        };
        continue;
      }

      customActions.push(action);
    }

    const customTargets: ('action3' | 'action4' | 'action5')[] = ['action3', 'action4', 'action5'];
    customTargets.forEach((target, index) => {
      const action = customActions[index];
      if (!action) {
        return;
      }

      defaults[target] = {
        enabled: true,
        name: action.name?.trim() || action.identifier?.trim() || '',
        value: action.url?.trim() ?? '',
      };
    });

    return defaults;
  }

  private createDefaultActions(): Pick<
    EditNumeronFormModel,
    'action1' | 'action2' | 'action3' | 'action4' | 'action5'
  > {
    return {
      action1: {
        enabled: false,
        name: 'Call Now',
        value: '',
      },
      action2: {
        enabled: false,
        name: 'Send Message',
        value: '',
      },
      action3: {
        enabled: false,
        name: '',
        value: '',
      },
      action4: {
        enabled: false,
        name: '',
        value: '',
      },
      action5: {
        enabled: false,
        name: '',
        value: '',
      },
    };
  }
}
